import { useMemo } from 'react'
import { ColDef, ColGroupDef } from 'ag-grid-community'
import { intl } from '../../../../../i18n'
import { PermissionPolicyOption } from '../../../../../domain/value-object/PermissionPolicy'
import { ApplicationFunctionPermissionRow } from '../../types'
import { OperationI18nLabelKey } from '../../../../../domain/value-object/ScopeVO'
import { useRowNumber } from '../../../../page-properties/bulksheet-properties/rowNumber'

type UseColumnDefsProps = {
  permissionPolicyOptions: PermissionPolicyOption[]
}

export const useColumnDefs = ({
  permissionPolicyOptions,
}: UseColumnDefsProps): (
  | ColDef<ApplicationFunctionPermissionRow>
  | ColGroupDef<ApplicationFunctionPermissionRow>
)[] => {
  const rowNumberCol = useRowNumber()

  const columnDefs = useMemo(() => {
    const cols: (
      | ColDef<ApplicationFunctionPermissionRow>
      | ColGroupDef<ApplicationFunctionPermissionRow>
    )[] = [
      rowNumberCol,
      {
        headerName: intl.formatMessage({
          id: 'permissionPolicies.columnGroup.screenInfo',
        }),
        children: [
          {
            field: 'directoryName',
            headerName: intl.formatMessage({
              id: 'permissionPolicies.column.directory',
            }),
            width: 250,
            pinned: 'left',
            floatingFilter: true,
            editable: false,
            filter: 'agSetColumnFilter',
            valueGetter: params => {
              return (
                params.data?.directoryName ||
                intl.formatMessage({ id: 'permissionPolicies.personalMenu' })
              )
            },
          },
          {
            field: 'applicationFunctionExternalId',
            headerName: intl.formatMessage({
              id: 'permissionPolicies.column.applicationFunctionId',
            }),
            width: 200,
            pinned: 'left',
            hide: true,
            floatingFilter: true,
            editable: false,
            filter: 'agSetColumnFilter',
          },
          {
            field: 'applicationFunctionName',
            headerName: intl.formatMessage({
              id: 'permissionPolicies.column.applicationFunctionName',
            }),
            width: 250,
            pinned: 'left',
            floatingFilter: true,
            editable: false,
            filter: 'agSetColumnFilter',
          },
        ],
      },
      {
        headerName: intl.formatMessage({
          id: 'permissionPolicies.columnGroup.permissionPolicies',
        }),
        children: permissionPolicyOptions.map(policy => ({
          field: `scopeByPermissionPolicyUuid.${policy.uuid}`,
          headerName: policy.name,
          headerTooltip: policy.description,
          width: 150,
          valueGetter: params => {
            const scope =
              params.data?.scopeByPermissionPolicyUuid?.[policy.uuid]
            if (!scope) {
              return ''
            }
            const i18nKey = OperationI18nLabelKey[scope.operation]
            return i18nKey ? intl.formatMessage({ id: i18nKey }) : ''
          },
          cellStyle: params => {
            const scope =
              params.data?.scopeByPermissionPolicyUuid?.[policy.uuid]
            const hasPermission = !!scope
            return hasPermission ? undefined : { backgroundColor: '#bdbdbd' }
          },
          filter: 'agSetColumnFilter',
          floatingFilter: true,
          editable: false,
        })),
      },
    ]

    return cols
  }, [permissionPolicyOptions, rowNumberCol])

  return columnDefs
}
